import {
  hasPlanningHourFormat,
  hasPlanningMileageFormat,
  hasPlanningStartDateFormat,
  normalizeExcelHeader,
} from "@qivia/ui/src/format";
import { columnByNormalizedHeader } from "./driverPlanningsConnectors";
import {
  driverPlanningHeaderColumns,
  type DriverPlanningInputLine,
  requiredDriverPlanningColumns,
} from "./driverPlanningsHeaders";

type DriverPlanningColumn = keyof DriverPlanningInputLine;

const getDriverPlanningHeaderLabel = (field: string): string =>
  driverPlanningHeaderColumns.find((column) => column.field === field)
    ?.acceptedHeaders[0] ?? field;

export type DriverPlanningImportError = {
  cell: string;
  content: string;
  errorType:
    | "missingHeader"
    | "missingValue"
    | "startDateFormat"
    | "hourFormat"
    | "mileageFormat";
};

export const getDriverPlanningHeaderErrors = (
  headers: unknown[],
): DriverPlanningImportError[] => {
  const errors: DriverPlanningImportError[] = [];
  const columnsFound = new Set<string>();

  headers.forEach((header) => {
    if (!header) return;
    const column =
      columnByNormalizedHeader[normalizeExcelHeader(String(header))];

    if (column) {
      columnsFound.add(column);
    }
  });

  requiredDriverPlanningColumns.forEach((column) => {
    if (columnsFound.has(column)) return;
    errors.push({
      cell: "",
      content: getDriverPlanningHeaderLabel(column),
      errorType: "missingHeader",
    });
  });

  return errors;
};

const getExcelCell = (index: number): string => {
  let columnNumber = index + 1;
  let columnName = "";

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return columnName;
};

const getFormatError = (
  column: DriverPlanningColumn,
  value: string,
): DriverPlanningImportError["errorType"] | null => {
  if (column === "startDate" && !hasPlanningStartDateFormat(value)) {
    return "startDateFormat";
  }

  if (
    (column === "startTime" || column === "endTime") &&
    !hasPlanningHourFormat(value)
  ) {
    return "hourFormat";
  }

  if (column === "expectedMileage" && !hasPlanningMileageFormat(value)) {
    return "mileageFormat";
  }

  return null;
};

export const getDriverPlanningLineErrors = (
  rows: unknown[][],
): DriverPlanningImportError[] => {
  const headers = rows[0] ?? [];
  const columns = headers.flatMap((header, index) => {
    if (!header) return [];
    const column =
      columnByNormalizedHeader[normalizeExcelHeader(String(header))];
    if (!column) return [];
    return [{ index, column }];
  });

  return rows.slice(1).flatMap((row, rowIndex) => {
    //ignore empty rows
    const hasContent = row.some(
      (value) =>
        value !== undefined && value !== null && String(value).trim() !== "",
    );
    if (!hasContent) return [];

    //get excel row number
    const excelRow = rowIndex + 2;

    return columns.flatMap(({ index, column }) => {
      const cell = `${getExcelCell(index)}${excelRow}`;
      const value = row[index] ? String(row[index]) : "";

      if (requiredDriverPlanningColumns.includes(column) && !value) {
        return [
          {
            cell,
            content: getDriverPlanningHeaderLabel(column),
            errorType: "missingValue" as const,
          },
        ];
      }

      const formatError = getFormatError(column, value);
      if (value && formatError) {
        return [
          {
            cell,
            content: value,
            errorType: formatError,
          },
        ];
      }

      return [];
    });
  });
};
